/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { task } from 'ember-concurrency';

import FiltersService from 'cio/services/filters';
import SegmentModel from 'cio/models/segment';
import DS from 'ember-data';
import { tracked } from '@glimmer/tracking';
import ShortSegmentModel from 'cio/models/short-segment';

interface CioSegmentLabelArgs {
  segment?: SegmentModel | Promise<SegmentModel>;
  segmentId?: string;
}

export default class CioSegmentLabel extends Component<CioSegmentLabelArgs> {
  @service filters!: FiltersService;
  @service store!: DS.Store;

  // In order to display the segment's icon and label, we can use either the
  // full Segment model, or the less detailed ShortSegment - they both contain
  // the icon and name properties.
  // We'll first attempt loading the Segment from the store, and if that doesn't
  // exist - ShortSegment. If neither exist in the store, we ask the API for the
  // full Segment model.
  @tracked segment: SegmentModel | ShortSegmentModel | null = null;
  @tracked fullSegmentForPopover: SegmentModel | null = null;
  @tracked isMissing = false;
  @tracked isFullSegmentMissing = false;

  get icon() {
    if (this.fetchSegment.isRunning || this.segment == null) {
      return 'segment';
    }

    return this.segment.icon;
  }

  fetchSegment = task({ restartable: true }, async () => {
    try {
      let segment: SegmentModel | null = null;

      if (this.args.segment != null) {
        segment = await this.args.segment;
      }

      if (this.args.segmentId != null) {
        segment =
          this.store.peekRecord('segment', this.args.segmentId) ??
          this.store.peekRecord('short-segment', this.args.segmentId) ??
          (await this.store.findRecord('segment', this.args.segmentId));
      }

      if (segment == null) {
        this.isMissing = true;
      } else {
        this.isMissing = false;
      }

      this.segment = segment;

      if (segment instanceof SegmentModel) {
        this.fullSegmentForPopover = segment;
      }
    } catch (e) {
      if (e?.errors[0]?.status === '404' || e?.status === '404') {
        this.isMissing = true;
        return;
      }

      throw e;
    }
  });

  fetchFullSegmentForPopover = task({ restartable: true }, async () => {
    let { segment } = this;
    if (segment instanceof ShortSegmentModel) {
      try {
        let fullSegmentForPopover =
          this.store.peekRecord('segment', segment.id) ?? (await this.store.findRecord('segment', segment.id));
        if (fullSegmentForPopover == null) {
          this.isFullSegmentMissing = true;
        } else {
          this.isFullSegmentMissing = false;
        }

        this.fullSegmentForPopover = fullSegmentForPopover;
      } catch (e) {
        if (e?.errors[0]?.status === '404' || e?.status === '404') {
          this.isMissing = true;
          return;
        }

        throw e;
      }
    }
  });
}
